import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { fetchSystemAttributes, putSystemAttributes } from "@/api/endpoints";
import { CommaListInput } from "@/components/CommaListInput";
import { InfoTip, type InfoTipContent } from "@/components/InfoTip";
import { useAuth } from "@/hooks/useAuth";
import { TIPS } from "@/lib/glossary";
import type { SystemAttributesWrite } from "@/types/api";

type BoolKey =
  | "uses_ozone"
  | "uses_clo2"
  | "uses_chemical_disinfectant"
  | "asbestos_susceptible";

const FIELDS: { key: BoolKey; label: string; tip: InfoTipContent }[] = [
  { key: "uses_chemical_disinfectant", label: "Uses chemical disinfectant", tip: TIPS.attrChemDisinfectant },
  { key: "uses_ozone", label: "Uses ozone", tip: TIPS.attrOzone },
  { key: "uses_clo2", label: "Uses chlorine dioxide", tip: TIPS.attrClo2 },
  { key: "asbestos_susceptible", label: "Asbestos pipe / susceptible", tip: TIPS.attrAsbestos },
];

function toTri(b: boolean | null): "unknown" | "yes" | "no" {
  return b === null ? "unknown" : b ? "yes" : "no";
}

function fromTri(t: string): boolean | null {
  return t === "yes" ? true : t === "no" ? false : null;
}

export function SystemAttributesCard({ pwsId }: { pwsId: string }): JSX.Element {
  const auth = useAuth();
  const canEdit = auth.scope === "platform";
  const qc = useQueryClient();
  const [draft, setDraft] = useState<SystemAttributesWrite | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["system-attributes", pwsId],
    queryFn: () => fetchSystemAttributes(pwsId),
    enabled: pwsId.length > 0,
  });

  const save = useMutation({
    mutationFn: (body: SystemAttributesWrite) => putSystemAttributes(pwsId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["system-attributes", pwsId] });
      // Attributes feed the computed schedule (source, treatment) — without
      // this the schedule card shows stale rows/labels until a page reload.
      await qc.invalidateQueries({ queryKey: ["monitoring-schedule", pwsId] });
      setDraft(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Save failed"),
  });

  function startEdit(): void {
    const a = query.data;
    setError(null);
    setDraft({
      uses_ozone: a?.uses_ozone ?? null,
      uses_clo2: a?.uses_clo2 ?? null,
      uses_chemical_disinfectant: a?.uses_chemical_disinfectant ?? null,
      asbestos_susceptible: a?.asbestos_susceptible ?? null,
      source: a?.source ?? null,
      waivers: a?.waivers ?? null,
    });
  }

  if (query.isLoading) {
    return (
      <section className="card">
        <h2 className="card-title">System attributes</h2>
        <div className="skeleton h-12" aria-label="Loading attributes" />
      </section>
    );
  }

  // A failed fetch must not masquerade as "attributes not set yet".
  if (query.isError) {
    return (
      <section className="card">
        <h2 className="card-title">System attributes</h2>
        <p className="text-sm text-severity-high" role="alert">
          Couldn&apos;t load attributes.
        </p>
        <button
          type="button"
          className="btn-primary text-xs mt-2"
          onClick={() => void query.refetch()}
        >
          Retry
        </button>
      </section>
    );
  }

  const a = query.data;

  return (
    <section className="card">
      <header className="flex items-baseline justify-between mb-1">
        <h2 className="card-title mb-0 inline-flex items-center">
          System attributes
          <InfoTip {...TIPS.sysAttributes} />
        </h2>
        {canEdit && !draft && (
          <button type="button" className="btn-primary py-1 px-2 text-xs" onClick={startEdit}>
            Edit
          </button>
        )}
      </header>
      <p className="text-xs text-ink-400 mb-3">
        Facts FDEP doesn&apos;t publish — set them so the right rules apply.
      </p>

      {draft ? (
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-ink-200 inline-flex items-center">
                {f.label}
                <InfoTip {...f.tip} />
              </span>
              <select
                className="input max-w-[140px]"
                value={toTri(draft[f.key])}
                onChange={(e) => setDraft({ ...draft, [f.key]: fromTri(e.target.value) })}
              >
                <option value="unknown">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          ))}
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="text-ink-200 inline-flex items-center">
              Water source
              <InfoTip {...TIPS.attrSource} />
            </span>
            <select
              className="input max-w-[140px]"
              value={draft.source ?? "unknown"}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  source: e.target.value === "GW" || e.target.value === "SW" ? e.target.value : null,
                })
              }
            >
              <option value="unknown">Not confirmed</option>
              <option value="GW">Ground water</option>
              <option value="SW">Surface water</option>
            </select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200 inline-flex items-center">
              Waivers (comma-sep)
              <InfoTip {...TIPS.attrWaivers} />
            </span>
            <CommaListInput
              value={draft.waivers ?? null}
              onChange={(waivers) => setDraft({ ...draft, waivers })}
            />
          </label>
          {error && <p className="text-sm text-severity-high">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
            <button type="button" className="text-xs text-ink-300" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {FIELDS.map((f) => {
            const val = a ? a[f.key] : null;
            return (
              <div key={f.key} className="contents">
                <dt className="text-ink-300 inline-flex items-center">
                  {f.label}
                  <InfoTip {...f.tip} />
                </dt>
                <dd>
                  {val === null ? (
                    <span className="pill-medium">needs input</span>
                  ) : val ? (
                    "Yes"
                  ) : (
                    "No"
                  )}
                </dd>
              </div>
            );
          })}
          <dt className="text-ink-300 inline-flex items-center">Water source<InfoTip {...TIPS.attrSource} /></dt>
          <dd>
            {a?.source === "GW" ? (
              "Ground water"
            ) : a?.source === "SW" ? (
              "Surface water"
            ) : (
              <span className="pill-medium">assumed GW</span>
            )}
          </dd>
          <dt className="text-ink-300 inline-flex items-center">Waivers<InfoTip {...TIPS.attrWaivers} /></dt>
          <dd>
            {a?.waivers && a.waivers.length > 0 ? (
              a.waivers.join(", ")
            ) : (
              <span className="text-ink-400">—</span>
            )}
          </dd>
        </dl>
      )}
    </section>
  );
}
